import { DownloadOutlined, FileAddOutlined, LeftOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Modal, Spin, Table, Tabs, Upload } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MeasurementsErrorContext, UpdateMeasurementsErrorContext } from "../../../context/measurementErrorContext";
import { MEASUREMENT_LIST, PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../util/helper";
import { UpdateCompanyContext } from "../../../context/companyContext";
import Column from "antd/es/table/Column";
import ColumnGroup from "antd/es/table/ColumnGroup";
import { clientFetch } from "../../../utils/clientFetch";
import { parseCsvErrorPayload, showCsvUploadErrorModal } from "../../../utils/csvErrorModal";

// タブ内で表示する各測定項目のエラー情報の型定義
export type ErrorDetail = {
  key: string;
  label: string;
  value: string;
};

export type ErrorSettingsType = {
  measure_name: string;
  error: ErrorDetail[];
};

// ブランド毎にグループ化したデータの型定義
type GroupedMeasurementError = {
  brand_name: string;
  settings: ErrorSettingsType[];
};

export function MeasurementsError() {
  const { measurementsError } = useContext(MeasurementsErrorContext)
  const { refreshMeasurementsError } = useContext(UpdateMeasurementsErrorContext)
  const { refreshCompany } = useContext(UpdateCompanyContext)
  const navigate = useNavigate()
  const { companyId } = useParams()
  const [isUploading, setIsUploading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadMeasurementsError = async () => {
      setIsPageReady(false)

      if (!companyId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      try {
        const companyResult = await refreshCompany(companyId)

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return
        }

        const measurementsErrorResult = await refreshMeasurementsError(companyId)

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(measurementsErrorResult, navigate)) {
          return
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating measurements error.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadMeasurementsError()

    return () => {
      isCancelled = true
    }
  }, [companyId, navigate, refreshCompany, refreshMeasurementsError])

  const brands = Array.from(new Set(measurementsError.map((item => item.brand_name))))

  // ブランド毎に MEASUREMENT_LIST 順にsettingsを作成し「ボタン間隔」を含む項目があれば末尾に追加
  const groupedArray: GroupedMeasurementError[] = brands.map((brand) => {
    const brandItems = measurementsError.filter((item => item.brand_name === brand))

    // MEASUREMENT_LIST で定義された項目
    const baseSettings: ErrorSettingsType[] = MEASUREMENT_LIST.map((measureName) => {
      const matched = brandItems.find(item => item.measure_name === measureName)
      return {
        measure_name: measureName,
        error: [
          { key: 'plus', label: "プラス", value: matched ? matched.error_plus : '-' },
          { key: 'minus', label: "マイナス", value: matched ? matched.error_minus : '-' },
        ],
      }
    })

    // 「ボタン間隔」を含む追加項目を抽出
    const extraItems = brandItems.filter(item => item.measure_name.startsWith('ボタン間隔') && item.measure_name !== 'ボタン間隔')
    const extraSettings: ErrorSettingsType[] = extraItems.map((item) => ({
      measure_name: item.measure_name,
      error: [
        { key: 'plus', label: "プラス", value: item.error_plus },
        { key: 'minus', label: "マイナス", value: item.error_minus },
      ],
    }))

    // 動的に末尾へマージ
    const settings = [...baseSettings, ...extraSettings]

    return { brand_name: brand, settings }
  })

  const onDownloadCsv = useCallback(async () => {
    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/measurement-error/download/${companyId}`, {
        method: "GET",
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'MeasurementsError.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error) {
      console.error("Error fetching measurements error.");
    }
  }, [companyId])

  const onUploadCsv = useCallback(async (options: any) => {
    const { file, onSuccess, onError } = options
    if (!companyId) return
    setIsUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/measurement-error/upload/${companyId}`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const payload = await parseCsvErrorPayload(response);
        showCsvUploadErrorModal(file.name, payload);
        onError('error');
        return;
      }

      const result = await response.json()
      const measurementsErrorResult = await refreshMeasurementsError(companyId)
      if (shouldStopForInvalidContextFetchResult(measurementsErrorResult, navigate)) {
        onError('error')
        return
      }
      onSuccess(result)
      Modal.success({ content: '登録が完了しました', centered: true })
    } catch (error) {
      showCsvUploadErrorModal(file.name)
      onError('error')
    } finally {
      setIsUploading(false)
    }
  }, [refreshMeasurementsError, companyId, navigate])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip="読み込み中..." />
      </Flex>
    )
  }

  return (
    <div>
      <Spin spinning={isUploading} tip="アップロード中..." size="large" fullscreen />
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">戻る</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">設定誤差一覧画面</p>
      </div>
      <Divider />
      <div className="flex justify-center w-full">
        <Tabs
          animated
          indicator={{ size: 30, align: 'center' }}
          tabPosition="left"
          style={{ height: '505px', width: '600px' }}
          items={groupedArray.map((group) => ({
            label: group.brand_name,
            key: group.brand_name,
            children: <TabChildren settings={group.settings} />
          }))}
        />
      </div>
      <Divider />
      <Flex justify="center" className="space-x-[50px]">
        <Button
          variant="solid"
          color="primary"
          icon={<DownloadOutlined />}
          onClick={onDownloadCsv}
          className="w-[200px]"
          disabled={isUploading}
        >
          CSVダウンロード
        </Button>
        <Upload
          customRequest={onUploadCsv}
          maxCount={1}
          accept=".csv"
          itemRender={(originNode, file, currFileList) => (
            <div className="w-[200px]">{originNode}</div>
          )}
        >
          <Button
            variant="solid"
            color="primary"
            icon={<FileAddOutlined />}
            className="w-[200px]"
            loading={isUploading}
          >
            CSVアップロード
          </Button>
        </Upload>
      </Flex>
    </div>
  )
}

function TabChildren({ settings }: { settings: ErrorSettingsType[] }): JSX.Element {
  return (
    <Table
      dataSource={settings}
      bordered
      rowKey={(record) => record.measure_name}
      pagination={false}
      scroll={{ y: 400 }}
    >
      <Column title='採寸箇所' dataIndex="measure_name" key="measure_name" />
      <ColumnGroup title='設定誤差'>
        <Column
          title='プラス'
          key='plus'
          render={(_, record: ErrorSettingsType) => {
            const plus = record.error.find((e) => e.key === 'plus')
            return plus?.value ?? '-'
          }}
        />
        <Column
          title='マイナス'
          key='minus'
          render={(_, record: ErrorSettingsType) => {
            const minus = record.error.find((e) => e.key === 'minus')
            return minus?.value ?? '-'
          }}
        />
      </ColumnGroup>
    </Table>
  )
}
